// --- Dependencies -------------------------------------------------------------------------------
import { MenuItem, TextField } from "@mui/material";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import { getFilterFieldSx, getCellMenuProps } from "@/utils/inputSx";

// --- Types --------------------------------------------------------------------------------------

export type TyFilterOption = { value: string; label: string };

type Props = {
    label: string;
    value: string;
    options: TyFilterOption[];
    onChange: (value: string) => void;
    /** Hidden when the filter is one the screen always shows — most are. */
    anyLabel?: string;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * One dropdown in the filter bar. A thin wrapper over MUI so the six screens do not each configure a
 * select — and so the filter bar matches the cells, which are MUI too. A native `<select>` here would
 * be the one control in the dashboard that renders as the operating system rather than as the app.
 *
 * The empty value is a real option labelled "Any" rather than a blank line, because a dropdown whose
 * first entry looks empty reads as broken rather than as unset.
 */
export default function FilterSelect({ label, value, options, onChange, anyLabel }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const dir = lang === "en" ? "ltr" : "rtl";

    return (
        <TextField
            select
            size="small"
            label={label}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            sx={getFilterFieldSx(dir)}
            slotProps={{ select: { MenuProps: getCellMenuProps(dir) } }}
        >
            <MenuItem value="">{anyLabel ?? t.table.any}</MenuItem>
            {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );
}
